import React, { useEffect, useState } from "react";
import { Elements, useStripe } from "@stripe/react-stripe-js";
import { call } from "services/axiosService/core";
import getStripe from "./getStripe";
import { useMaterialUIController } from "context";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import * as Sentry from "@sentry/browser";
import { useTranslation } from "react-i18next";

function Inner3DSConfirm() {
  const stripe = useStripe();
  const { t } = useTranslation();
  const [status, setStatus] = useState("confirming"); // confirming | success | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!stripe) return;

    const params = new URLSearchParams(window.location.search);
    const clientSecret = params.get("pi_cs");
    const userId = params.get("uid");

    if (!clientSecret || !userId) {
      setStatus("error");
      setMessage(t("messages.subscription.threeDsInvalidLink"));
      return;
    }

    stripe
      .handleNextAction({ clientSecret })
      .then(({ paymentIntent, error }) => {
        if (error) {
          setStatus("error");
          setMessage(error.message || t("messages.subscription.threeDsUnexpectedError"));
          return;
        }

        if (paymentIntent?.status === "succeeded") {
          return call(`payments/complete-3ds/${userId}`, "PUT", {
            body: { intent_id: paymentIntent.id },
          }).then((response) => {
            if (response.status === 200) {
              setStatus("success");
            } else {
              setStatus("error");
              setMessage(t("messages.subscription.threeDsNotFinalized"));
            }
          });
        } else {
          setStatus("error");
          setMessage(t("messages.subscription.threeDsNotConfirmed"));
        }
      })
      .catch((err) => {
        Sentry.captureException(err);
        setStatus("error");
        setMessage(t("messages.subscription.threeDsUnexpectedError"));
      });
  }, [stripe]);

  return (
    <Dialog open>
      {status === "confirming" && (
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "200px",
            minWidth: "300px",
            gap: 2,
            padding: 4,
          }}
        >
          <CircularProgress />
          <Typography variant="h6" textAlign="center">
            {t("loaderText.completingAuthentication")}
          </Typography>
        </DialogContent>
      )}

      {status === "success" && (
        <>
          <DialogContent sx={{ minWidth: "300px", padding: 4 }}>
            <Typography variant="h5" textAlign="center" mb={1}>
              {t("headings.paymentVerified")}
            </Typography>
            <Typography variant="body1" textAlign="center" color="text.secondary">
              {t("messages.subscription.threeDsSuccess")}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
            <MDButton
              onClick={() => (window.location.href = "/profile")}
              style={{ color: "white", background: "#10B0D5" }}
            >
              {t("buttonTitles.goToProfile")}
            </MDButton>
          </DialogActions>
        </>
      )}

      {status === "error" && (
        <>
          <DialogContent sx={{ minWidth: "300px", padding: 4 }}>
            <Typography variant="h5" textAlign="center" mb={1}>
              {t("headings.authenticationFailed")}
            </Typography>
            <Typography variant="body1" textAlign="center" color="error">
              {message}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
            <MDButton
              onClick={() => (window.location.href = "/profile")}
              style={{ color: "white", background: "#10B0D5" }}
            >
              {t("buttonTitles.goToProfile")}
            </MDButton>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

export default function ThreeDSConfirmPage() {
  const { t } = useTranslation();
  const [controller] = useMaterialUIController();
  const { profile } = controller;
  const [stripePromise, setStripePromise] = useState(null);

  useEffect(() => {
    call(`payments/config/${profile.id}`, "GET", {})
      .then((response) => {
        if (response.status === 200 && response.data.publish_key) {
          setStripePromise(getStripe(response.data.publish_key));
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
      });
  }, []);

  const clientSecret = new URLSearchParams(window.location.search).get("pi_cs");

  return (
    <DashboardLayout>
      <DashboardNavbar title={t("pageTitles.completePayment")} />
      {stripePromise && clientSecret ? (
        <Elements stripe={stripePromise}>
          <Inner3DSConfirm />
        </Elements>
      ) : (
        <Dialog open={!stripePromise}>
          <DialogContent
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "200px",
              minWidth: "300px",
              gap: 2,
              padding: 4,
            }}
          >
            <CircularProgress />
            <Typography variant="h6" textAlign="center">
              {t("loaderText.loadingPaymentDetails")}
            </Typography>
          </DialogContent>
        </Dialog>
      )}
    </DashboardLayout>
  );
}
